(() => {
  const initialized = new WeakMap();

  const init = (root) => {
    const slides = Array.from(root.querySelectorAll("[data-arpro-carousel-slide]"));
    const dots = Array.from(root.querySelectorAll("[data-arpro-carousel-dot]"));
    const previous = root.querySelector("[data-arpro-carousel-previous]");
    const next = root.querySelector("[data-arpro-carousel-next]");
    const counter = root.querySelector("[data-arpro-carousel-counter]");
    const viewport = root.querySelector("[data-arpro-carousel-viewport]");
    const playback = root.querySelector("[data-arpro-carousel-playback]");
    const existing = initialized.get(root);
    if (existing && existing.viewport === viewport && existing.next === next) return;
    if (existing) existing.controller.abort();
    if (slides.length < 2 || !previous || !next || !viewport) return;
    const controller = new AbortController();
    const signal = controller.signal;
    let current = Math.max(0, slides.findIndex((slide) => !slide.hidden));
    const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const interval = Math.max(5000, Math.min(15000, Number(root.dataset.arproCarouselInterval) || 8000));
    const editor = root.dataset.arproCarouselEditor === "true" || !!window.Shopify?.designMode;
    let paused = root.dataset.arproCarouselAutoplay !== "true" || !!motion?.matches || editor;
    let hovered = false;
    let focused = root.contains(document.activeElement);
    let resumeFromControl = false;
    let inView = !window.IntersectionObserver;
    let timer;
    const stopTimer = () => { window.clearTimeout(timer); timer = undefined; };
    const canPlay = () => !editor && !paused && !hovered && inView && !document.hidden && root.isConnected &&
      (!focused || (resumeFromControl && document.activeElement === playback)) && !root.querySelector("dialog[open]");

    const schedule = () => {
      stopTimer();
      const playing = canPlay();
      root.dataset.arproCarouselState = playing ? "playing" : "paused";
      if (counter) counter.setAttribute("aria-live", playing ? "off" : "polite");
      if (playback) {
        playback.hidden = editor;
        playback.textContent = paused ? playback.dataset.playLabel : playback.dataset.pauseLabel;
        playback.setAttribute("aria-label", paused ? playback.dataset.playDescription : playback.dataset.pauseDescription);
      }
      if (!playing || signal.aborted) return;
      timer = window.setTimeout(() => {
        if (!root.isConnected) { controller.abort(); return; }
        if (canPlay()) show(current + 1);
        schedule();
      }, interval);
    };

    const sizeViewport = () => {
      const wasHidden = slides.map((slide) => slide.hidden);
      slides.forEach((slide) => { slide.hidden = false; });
      const tallest = Math.max(...slides.map((slide) => slide.getBoundingClientRect().height));
      slides.forEach((slide, index) => { slide.hidden = wasHidden[index]; });
      viewport.style.minHeight = `${Math.ceil(tallest)}px`;
    };

    const show = (index) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === current;
        slide.hidden = !selected;
        slide.classList.toggle("is-current", selected);
        slide.setAttribute("aria-hidden", String(!selected));
      });
      dots.forEach((dot, dotIndex) => {
        const selected = dotIndex === current;
        dot.classList.toggle("is-current", selected);
        if (selected) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
      if (counter) counter.textContent = `${current + 1} / ${slides.length}`;
      slides[(current + 1) % slides.length].querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = "eager"; });
    };

    const navigate = index => { show(index); schedule(); };
    previous.addEventListener("click", () => navigate(current - 1), {signal});
    next.addEventListener("click", () => navigate(current + 1), {signal});
    dots.forEach((dot) => dot.addEventListener("click", () => navigate(Number(dot.dataset.arproCarouselDot)), {signal}));
    viewport.addEventListener("keydown", (event) => {
      if (event.target !== viewport) return;
      if (event.key === "ArrowLeft") { event.preventDefault(); navigate(current - 1); }
      if (event.key === "ArrowRight") { event.preventDefault(); navigate(current + 1); }
      if (event.key === "Home") { event.preventDefault(); navigate(0); }
      if (event.key === "End") { event.preventDefault(); navigate(slides.length - 1); }
    }, {signal});
    playback?.addEventListener("click", () => { if (editor) return; paused = !paused; resumeFromControl = !paused; schedule(); }, {signal});
    root.addEventListener("pointerenter", event => { if (event.pointerType === "touch") return; hovered = true; schedule(); }, {signal});
    root.addEventListener("pointerleave", () => { hovered = false; schedule(); }, {signal});
    root.addEventListener("focusin", () => { focused = true; resumeFromControl = false; schedule(); }, {signal});
    root.addEventListener("focusout", event => { focused = root.contains(event.relatedTarget); schedule(); }, {signal});
    document.addEventListener("visibilitychange", schedule, {signal});
    motion?.addEventListener("change", event => { if (event.matches) paused = true; schedule(); }, {signal});
    const intersection = window.IntersectionObserver ? new IntersectionObserver(entries => {
      inView = entries.some(entry => entry.target === root && entry.isIntersecting);
      schedule();
    }) : null;
    intersection?.observe(root);
    const dialogs = new MutationObserver(schedule);
    dialogs.observe(root, {subtree: true, attributes: true, attributeFilter: ["open"]});
    signal.addEventListener("abort", () => { stopTimer(); intersection?.disconnect(); dialogs.disconnect(); }, {once: true});
    initialized.set(root, {viewport, next, controller});

    const showAnchor = () => {
      const anchor = location.hash.slice(1);
      const index = slides.findIndex(slide => slide.dataset.campaignAnchor === anchor);
      if (anchor && index >= 0) navigate(index);
    };
    window.addEventListener("hashchange", showAnchor, {signal});
    sizeViewport();
    window.addEventListener("resize", sizeViewport, {signal});
    root.querySelectorAll("img").forEach((img) => img.addEventListener("load", sizeViewport, {signal}));
    show(current);
    showAnchor();
    schedule();
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-arpro-campaign-carousel]')) init(scope);
    scope.querySelectorAll("[data-arpro-campaign-carousel]").forEach(init);
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initAll());
  else initAll();
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("arpro:sections-updated", (event) => initAll(event.detail?.root || document));
  document.addEventListener("shopify:section:unload", (event) => {
    const destroy = root => {initialized.get(root)?.controller.abort(); initialized.delete(root)};
    if (event.target.matches?.('[data-arpro-campaign-carousel]')) destroy(event.target);
    event.target.querySelectorAll('[data-arpro-campaign-carousel]').forEach(destroy);
  });
})();
